import { useMemo } from "react";

import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { isProjectListHydrated } from "~/projectListHydration";
import { useStore } from "~/store";
import {
  resolveBlockedBoardExit,
  resolveGroupLanding,
  resolveProjectRouteTarget,
  type BlockedBoardExit,
  type GroupLanding,
  type ProjectRouteInput,
  type ProjectRouteTarget,
} from "~/projectRouteTarget";
import { useProjectsAcrossEnvironments } from "./useProjectsAcrossEnvironments";

/**
 * What `/project/:key` should do, given whatever is in the URL.
 *
 * All the deciding lives in pure functions; this only supplies them with the
 * two independently-loading sources. Keeping the decision out of the component
 * is deliberate — every routing bug on this surface so far has lived in a
 * component's implicit branching, including an access gate that shipped
 * deciding before its data had loaded.
 */
export function useProjectRouteTarget(key: string): ProjectRouteTarget {
  const input = useProjectRouteInput(key);
  return useMemo(() => resolveProjectRouteTarget(input), [input]);
}

/** Where the tasks route should send a board the access gate evicted. */
export function useBlockedBoardExit(key: string): BlockedBoardExit {
  const input = useProjectRouteInput(key);
  return useMemo(() => resolveBlockedBoardExit(input), [input]);
}

/** The two independently loading sources every `/project/:key` decision reads. */
function useProjectRouteInput(key: string): ProjectRouteInput {
  const projects = useProjectsAcrossEnvironments();
  const intake = useCmmdWorkIntakeSnapshot();
  const environmentStateById = useStore((state) => state.environmentStateById);
  const hydrated = isProjectListHydrated(environmentStateById);
  return useMemo(
    () => ({
      key,
      projects,
      boards: intake.projects,
      // NOT hardcoded false. An empty list before any environment has
      // reported is indistinguishable from "no projects", and treating it as
      // the latter is what made every /project/:key bounce straight to /new
      // on first paint.
      loading: !hydrated,
      boardsLoading: intake.isLoading,
    }),
    [key, projects, intake.projects, intake.isLoading, hydrated],
  );
}

/** Where a resolved group should land: one board, a choice, or nothing yet. */
export function useGroupLanding(groupKey: string | null): GroupLanding {
  const intake = useCmmdWorkIntakeSnapshot();

  return useMemo(
    () => (groupKey === null ? { kind: "empty" } : resolveGroupLanding(groupKey, intake.projects)),
    [groupKey, intake.projects],
  );
}
